import { Component } from '@theme/component';

/* Sacred Skin browser-local shopping memory. No prices, variants, customer data or cart calls. */
(() => {
  'use strict';
  if (window.SacredSkinShoppingMemoryV1) return;
  const KEY = 'sacred-skin:shopping-memory:v1';
  const EVENT = 'sacred-skin:shopping-memory-change';
  const DAY = 86400000;
  const limits = { wishlist: 60, recent: 24 };
  const lifetimes = { wishlist: 180 * DAY, recent: 30 * DAY };
  let storageOK = true;
  const blank = () => ({ version: 1, wishlist: [], recent: [] });

  function safeRecord(input, now = Date.now()) {
    if (!input || typeof input !== 'object') return null;
    if (typeof input.handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,199}$/.test(input.handle)) return null;
    if (typeof input.title !== 'string' || !input.title.trim() || input.title.length > 500) return null;
    let url;
    try {
      if (typeof input.url !== 'string' || !input.url.startsWith('/') || input.url.startsWith('//')) return null;
      url = new URL(input.url, location.origin);
      if (url.origin !== location.origin || !url.pathname.endsWith('/products/' + input.handle)) return null;
      if (url.search || url.hash || url.username || url.password) return null;
    } catch { return null; }
    let image = '';
    if (typeof input.image === 'string' && input.image.length <= 2048 && input.image) {
      try {
        const parsed = new URL(input.image, location.origin);
        if (parsed.protocol === 'https:' && !parsed.username && !parsed.password &&
            (parsed.origin === location.origin || parsed.hostname === 'cdn.shopify.com')) image = parsed.href;
      } catch { /* A missing or invalid image does not prevent a product link. */ }
    }
    const time = Number(input.time);
    if (!Number.isFinite(time) || time <= 0 || time > now + 60000) return null;
    return { handle: input.handle, title: input.title.trim(), url: url.pathname, image, time };
  }

  function sanitize(input) {
    const result = blank();
    if (!input || input.version !== 1) return result;
    const now = Date.now();
    for (const kind of Object.keys(limits)) {
      const seen = new Set();
      const records = Array.isArray(input[kind]) ? input[kind].slice(0, 200) : [];
      result[kind] = records.map(record => safeRecord(record, now)).filter(record => {
        if (!record || now - record.time > lifetimes[kind] || seen.has(record.handle)) return false;
        seen.add(record.handle);
        return true;
      }).sort((a, b) => b.time - a.time).slice(0, limits[kind]);
    }
    return result;
  }

  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return blank();
      if (raw.length > 250000) return blank();
      return sanitize(JSON.parse(raw));
    } catch (error) {
      if (!(error instanceof SyntaxError)) storageOK = false;
      return blank();
    }
  }

  function write(value) {
    try {
      localStorage.setItem(KEY, JSON.stringify(sanitize(value)));
      storageOK = true;
      document.dispatchEvent(new CustomEvent(EVENT));
      return true;
    } catch { storageOK = false; return false; }
  }

  function fromElement(element) {
    return safeRecord({ handle: element.dataset.handle, title: element.dataset.title,
      url: element.dataset.productUrl, image: element.dataset.imageUrl || '', time: Date.now() });
  }
  function setMessage(element, selector, text) {
    const target = element.querySelector(selector);
    if (target) target.textContent = text;
  }
  function translated(element, key, title = '') {
    return (element.dataset[key] || '').replace('__SSC_PRODUCT_TITLE__', () => title);
  }
  function list(kind) { return read()[kind]; }
  function remove(kind, handle) {
    const value = read();
    value[kind] = value[kind].filter(item => item.handle !== handle);
    return write(value);
  }
  function clear(kind) { const value = read(); value[kind] = []; return write(value); }
  function remember(record) {
    if (!record) return false;
    const value = read();
    value.recent = [record, ...value.recent.filter(item => item.handle !== record.handle)];
    // Refresh previously saved title/image metadata without extending its retention.
    value.wishlist = value.wishlist.map(item => item.handle === record.handle ? { ...record, time: item.time } : item);
    return write(value);
  }

  window.SacredSkinShoppingMemoryV1 = { key: KEY };
  window.addEventListener('storage', event => {
    if (event.key === KEY || event.key === null) document.dispatchEvent(new CustomEvent(EVENT));
  });

  class SaveProduct extends Component {
    connectedCallback() {
      super.connectedCallback();
      this.initialize();
    }
    updatedCallback() {
      super.updatedCallback();
      this.initialize();
    }
    initialize() {
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      queueMicrotask(() => { if (this.controller === controller) this.setup(); });
    }
    disconnectedCallback() {
      this.controller?.abort();
      super.disconnectedCallback();
    }
    setup() {
      if (!this.isConnected || this.controller.signal.aborted) return;
      this.record = fromElement(this);
      this.button = this.querySelector('[data-save-toggle]');
      if (!this.record || !this.button) return;
      this.button.hidden = false;
      const signal = this.controller.signal;
      this.button.addEventListener('click', () => {
        const value = read();
        const saved = value.wishlist.some(item => item.handle === this.record.handle);
        value.wishlist = saved ? value.wishlist.filter(item => item.handle !== this.record.handle) :
          [{ ...this.record, time: Date.now() }, ...value.wishlist];
        if (write(value)) {
          this.update();
          setMessage(this, '[data-save-status]', translated(this, saved ? 'removedMessage' : 'savedMessage'));
        } else {
          setMessage(this, '[data-save-status]', translated(this, 'saveError'));
        }
      }, { signal });
      document.addEventListener(EVENT, () => this.update(), { signal });
      this.update();
    }
    update() {
      const saved = list('wishlist').some(item => item.handle === this.record.handle);
      this.button.setAttribute('aria-pressed', String(saved));
      setMessage(this, '[data-save-text]', translated(this, saved ? 'savedLabel' : 'saveLabel'));
    }
  }

  class ShoppingMemory extends Component {
    connectedCallback() {
      super.connectedCallback();
      this.initialize();
    }
    updatedCallback() {
      super.updatedCallback();
      this.initialize();
    }
    initialize() {
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      queueMicrotask(() => { if (this.controller === controller) this.setup(); });
    }
    disconnectedCallback() {
      this.controller?.abort();
      super.disconnectedCallback();
    }
    setup() {
      if (!this.isConnected || this.controller.signal.aborted) return;
      this.kind = this.dataset.mode === 'wishlist' ? 'wishlist' : 'recent';
      this.current = fromElement(this);
      this.grid = this.querySelector('[data-memory-list]');
      this.empty = this.querySelector('[data-memory-empty]');
      this.clearButton = this.querySelector('[data-clear-memory]');
      this.moreButton = this.querySelector('[data-memory-more]');
      this.batchSize = Math.max(2, Math.min(8, Number(this.dataset.limit) || 4));
      this.visibleCount = this.batchSize;
      if (!this.grid || !this.empty || !this.clearButton || !this.moreButton) return;
      const signal = this.controller.signal;
      this.clearButton.textContent = translated(this, this.kind === 'wishlist' ? 'clearWishlist' : 'clearHistory');
      this.clearButton.addEventListener('click', () => {
        if (clear(this.kind)) {
          this.visibleCount = this.batchSize;
          setMessage(this, '[data-memory-status]', translated(this, this.kind === 'wishlist' ? 'wishlistCleared' : 'historyCleared'));
          this.querySelector('h2')?.focus();
        } else setMessage(this, '[data-memory-status]', translated(this, 'clearError'));
      }, { signal });
      this.moreButton.addEventListener('click', () => {
        const previousCount = this.grid.children.length;
        this.visibleCount += this.batchSize;
        this.render();
        this.grid.children[previousCount]?.querySelector('a')?.focus();
      }, { signal });
      document.addEventListener(EVENT, () => this.render(), { signal });
      if (this.dataset.recordRecent === 'true' && this.current && !window.Shopify?.designMode) remember(this.current);
      this.render();
    }
    render() {
      const all = list(this.kind);
      const eligible = all.filter(item => this.kind !== 'recent' || item.handle !== this.current?.handle);
      const records = eligible.slice(0, this.visibleCount);
      this.grid.replaceChildren();
      this.grid.hidden = records.length === 0;
      this.empty.hidden = records.length > 0;
      this.clearButton.hidden = all.length === 0;
      this.moreButton.hidden = eligible.length <= records.length;
      const heading = this.querySelector('h2');
      if (heading) heading.tabIndex = -1;
      if (!storageOK) setMessage(this, '[data-memory-status]', translated(this, 'storageUnavailable'));
      for (const record of records) {
        const card = document.createElement('li'); card.className = 'ssc-memory__card';
        const link = document.createElement('a'); link.className = 'ssc-memory__product'; link.href = record.url;
        if (record.image) {
          const photo = document.createElement('img'); photo.className = 'ssc-memory__photo';
          photo.src = record.image; photo.alt = ''; photo.width = 480; photo.height = 480;
          photo.loading = 'lazy'; photo.decoding = 'async';
          photo.addEventListener('error', () => photo.remove(), { once: true }); link.append(photo);
        }
        const title = document.createElement('span'); title.className = 'ssc-memory__title'; title.textContent = record.title;
        link.append(title); card.append(link);
        const removeButton = document.createElement('button'); removeButton.type = 'button'; removeButton.className = 'ssc-memory-link';
        removeButton.textContent = translated(this, this.kind === 'wishlist' ? 'removeWishlist' : 'removeRecent');
        removeButton.setAttribute('aria-label', translated(this, this.kind === 'wishlist' ? 'removeWishlistLabel' : 'removeRecentLabel', record.title));
        removeButton.addEventListener('click', () => {
          if (remove(this.kind, record.handle)) {
            setMessage(this, '[data-memory-status]', translated(this, 'productRemoved', record.title));
            const focusTarget = this.querySelector('[data-memory-list] a') || this.querySelector('h2');
            focusTarget?.focus();
          } else setMessage(this, '[data-memory-status]', translated(this, 'updateError'));
        });
        card.append(removeButton); this.grid.append(card);
      }
    }
  }
  if (!customElements.get('sacred-save-product')) customElements.define('sacred-save-product', SaveProduct);
  if (!customElements.get('sacred-shopping-memory')) customElements.define('sacred-shopping-memory', ShoppingMemory);
})();
